import { forwardRef } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import Label from "./Label";

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  label?: ReactNode;
  description?: ReactNode;
}

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className,
      label,
      description,
      disabled,
      id,
      ...props
    },
    ref,
  ) => {
    return (
      <label
        htmlFor={id}
        className={cn(
          "inline-flex gap-3",
          description ? "items-start" : "items-center",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          className,
        )}
      >
        <span
          className={cn(
            "relative inline-flex h-5 shrink-0 items-center",
            description && "mt-0.5",
          )}
        >
          <input
            ref={ref}
            id={id}
            type="checkbox"
            role="switch"
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <span
            className={cn(
              "relative h-5 w-9 rounded-full bg-surface-300 shadow-[inset_0_1px_2px_rgb(0_0_0/0.08)] transition-colors dark:bg-surface-300",
              "after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow-[0_1px_2px_rgb(16_24_40/0.25)] after:transition-transform after:duration-200 after:content-['']",
              "peer-checked:bg-primary-600 peer-checked:after:translate-x-4",
              "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300",
              "peer-disabled:opacity-50",
              "peer-aria-invalid:ring-2 peer-aria-invalid:ring-error-500",
            )}
            aria-hidden
          />
        </span>
        {label || description ? (
          <span className="min-w-0">
            {label ? (
              <Label as="span" spacing="none" emphasis="semibold" className="leading-6">
                {label}
              </Label>
            ) : null}
            {description ? (
              <span className="mt-0.5 block text-xs leading-snug text-surface-500">
                {description}
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
    );
  },
);

Switch.displayName = "Switch";

export { Switch };
